import {UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators} from '@angular/forms';
import forEach from 'lodash-es/forEach';
import {ChoiceLayout, RenderSeparator} from '@ee/common/enums';
import {CustomFieldOption} from './custom-field.model';

export interface IntakeOption {
  show: boolean;
  required: boolean;
  /** Display mode for options that have one — for `eviction_reason`: 'RADIO' | 'MULTI_SELECT' (legacy null / 'SELECT' ⇒ 'RADIO'). */
  type?: string;
  /** The allowed subset of values, for options that offer a choice (e.g. `eviction_reason`). */
  options?: string[];
  /** `eviction_reason` only — the {value, label} choices behind `options`; absent ⇒ `options` with label = value. */
  option_definitions?: CustomFieldOption[];
  /** `eviction_reason` only — the pre-selection on a new intake: one value (RADIO) or a list (MULTI_SELECT). */
  default_value?: string | string[] | null;
  /** How selected values join into one string in documents; null means COMMA. */
  render_separator?: RenderSeparator | null;
  /** `eviction_reason` only — the reasons stacked or lined up and wrapped. Absent ⇒ VERTICAL. */
  choice_layout?: ChoiceLayout | null;
  more_info_link?: string;
  label_override?: string;
  hint?: string;
  hide_from_intake?: boolean;
}

/**
 * Built-in intake options retired by EE-392 Requirement 12: no intake renders or offers them. Their stored eviction
 * values, document tokens and read-only displays remain. An intake form saved before the migration `$unset` them may
 * still carry one as shown + required, so {@link processForm} must never require a control that has no input.
 */
export const RETIRED_INTAKE_OPTIONS: readonly string[] = [
  'applied_for_assistance',
  'not_applied_for_assistance',
  'eviction_reason_unpaid_rent',
  'civil_recovery_fees',
  'interest_rate',
  'interest_start_date'
];

/**
 * Adds validators for form controls per attorney's options/specifications
 *
 * @param form
 * @param options
 */
export function processForm(form: UntypedFormGroup, options: Map<string, IntakeOption>,
  standardValidators: Map<string, ValidatorFn[]> = new Map()): UntypedFormGroup {

  forEach(form.controls, (a: UntypedFormControl, b: string) => {
    const o = options[b] as IntakeOption;
    const validators = standardValidators.get(b) ?? [];

    if (o && o.required && o.show && !RETIRED_INTAKE_OPTIONS.includes(b)) {
      validators.push(Validators.required);
    }

    a.setValidators(validators);
    a.updateValueAndValidity();
  });

  return form;
}
